import { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import clsx from "clsx";
import { db, eq, uploadPublic } from "../lib/api";
import { invalidate, useDebounced, useQuery } from "../lib/hooks";
import { navigate } from "../lib/router";
import { useSession } from "../lib/session";
import { PageHead } from "../components/Shell";
import { Empty, ErrorBox, Field, Loading, Panel, Sheet, fmtDate, useToast } from "../components/ui";

type Block =
  | { type: "heading"; text: string }
  | { type: "text"; text: string }
  | { type: "image"; url: string; alt?: string; link?: string; caption?: string }
  | { type: "button"; label: string; url: string }
  | { type: "divider" };
type Audience = { clients: boolean; others: boolean; tags: string[]; exclude_tags: string[] };
type Issue = {
  id: string; title: string; subject: string; preheader: string | null; blocks: Block[]; audience: Audience;
  status: "Draft" | "Scheduled" | "Sending" | "Sent" | "Canceled"; send_on: string | null; daily_cap: number | null;
  error: string | null; created_at: string; started_at: string | null; finished_at: string | null;
};
type Stats = { sent?: number; delivered?: number; uniqueViews?: number; uniqueClicks?: number; unsubscriptions?: number; hardBounces?: number; softBounces?: number };
type Wave = { wave: number; date: string; count: number; status: string; error: string | null; sent_at: string | null; stats: Stats | null };
type Progress = { total: number; sent: number; in_wave: number; waiting: number; skipped: number; room_today: number; waves: Wave[] };
type NlSettings = { mailing_address: string; sender_name: string | null; sender_email: string | null; daily_cap: number; reserve: number; daily_limit: number;
  send_hour: number; brevo_connected: boolean; default_sender: string | null; room_today: number; consented: number };
type Count = { total: number; clients: number; others: number; per_day: number; days: number; with_email: number; no_consent: number };

const STATUS_PILL: Record<Issue["status"], string> = { Draft: "", Scheduled: "team", Sending: "leads", Sent: "warm", Canceled: "cold" };
const n = (v?: number | null) => (v ?? 0).toLocaleString("en-CA");
const pct = (a?: number, b?: number) => (b ? `${Math.round(((a ?? 0) / b) * 100)}%` : "–");
const sum = (ws: Wave[], k: keyof Stats) => ws.reduce((t, w) => t + (w.stats?.[k] ?? 0), 0);
const hourLabel = (h: number) => dayjs().hour(h).minute(0).format("h A");

function useNlSettings() {
  return useQuery("nl:settings", () => db.rpc<NlSettings>("newsletter_settings"));
}

// ───────────────────────── list ─────────────────────────
export function NewsletterList() {
  const { can } = useSession();
  const toast = useToast();
  const edit = can("newsletter", "edit");
  const issues = useQuery("nl:list", () => db.select<Issue>("newsletters", { select: "*", order: "created_at.desc" }));
  const st = useNlSettings();
  const [showSettings, setShowSettings] = useState(false);
  const [err, setErr] = useState<Error | null>(null);
  const s = st.data;

  async function create() {
    setErr(null);
    try {
      const month = dayjs().add(dayjs().date() > 20 ? 1 : 0, "month").format("MMMM");
      const [row] = await db.insert<Issue>("newsletters", {
        title: `${month} update`, subject: "",
        blocks: [
          { type: "heading", text: `What's new this ${month}` },
          { type: "text", text: "Write your opening here. Keep it short and personal.\n\nUse {first_name} for their first name and {date} for the day it's sent." },
          { type: "button", label: "Book a review", url: "" },
        ],
      });
      invalidate("nl:"); navigate(`/newsletter/${row.id}`);
    } catch (e) { setErr(e as Error); }
  }
  async function copy(id: string) {
    const nid = await db.rpc<string>("newsletter_copy", { p_id: id });
    invalidate("nl:"); toast("Copied as a new draft"); navigate(`/newsletter/${nid}`);
  }

  return (
    <>
      <PageHead help="newsletter" title="Newsletter" sub="Monthly emails to clients and contacts who agreed to hear from you."
        actions={<>
          <button className="btn" onClick={() => setShowSettings(true)}>Sending settings</button>
          {edit && <button className="btn primary" onClick={create}>New issue</button>}
        </>} />
      <ErrorBox error={err ?? issues.error ?? st.error} />
      {s && (!s.brevo_connected || !s.mailing_address) && (
        <div className="notice">
          {!s.brevo_connected && <div>Brevo isn't connected yet. Add the key in Supabase Vault (see Emails &amp; reminders).</div>}
          {!s.mailing_address && <div>Add your mailing address in <button className="linkish" onClick={() => setShowSettings(true)}>Sending settings</button>. Canadian anti-spam law requires it in every newsletter.</div>}
        </div>
      )}
      {s && (
        <div className="nl-stats">
          <div><b>{n(s.consented)}</b><span>can receive it</span></div>
          <div><b>{n(Math.min(s.daily_cap, s.daily_limit - s.reserve))}</b><span>sent per day at most</span></div>
          <div><b>{n(s.room_today)}</b><span>room left today</span></div>
          <div><b>{hourLabel(s.send_hour)}</b><span>daily waves start</span></div>
        </div>
      )}
      <Panel title="Issues">
        {issues.loading && !issues.data ? <Loading /> : !issues.data?.length ? (
          <Empty title="No issues yet">{edit ? "Click New issue to write your first one." : "Nothing has been written yet."}</Empty>
        ) : (
          <div className="table-wrap"><table className="data">
            <thead><tr><th>Issue</th><th>Status</th><th>Send date</th><th className="num">Reached</th><th></th></tr></thead>
            <tbody>
              {issues.data.map((i) => (
                <tr key={i.id} className="click" onClick={() => navigate(`/newsletter/${i.id}`)}>
                  <td><b>{i.subject || <span className="muted">(no subject yet)</span>}</b><div className="small muted">{i.title}</div></td>
                  <td><span className={clsx("pill", STATUS_PILL[i.status])}>{i.status}</span>{i.error && <div className="small" style={{ color: "var(--alert)" }}>Needs attention</div>}</td>
                  <td>{i.send_on ? fmtDate(i.send_on) : <span className="muted">–</span>}{i.finished_at && <div className="small muted">finished {fmtDate(i.finished_at)}</div>}</td>
                  <td className="num"><Reached id={i.id} status={i.status} /></td>
                  <td className="num" onClick={(e) => e.stopPropagation()}>{edit && <button className="btn small ghost" onClick={() => copy(i.id)}>Copy</button>}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </Panel>
      {showSettings && <SettingsSheet onClose={() => setShowSettings(false)} />}
    </>
  );
}

function Reached({ id, status }: { id: string; status: Issue["status"] }) {
  const p = useQuery(status === "Draft" || status === "Scheduled" ? null : `nl:progress:${id}`, () => db.rpc<Progress>("newsletter_progress", { p_id: id }));
  if (!p.data) return <span className="muted">–</span>;
  return <>{n(p.data.sent)} of {n(p.data.total)}{p.data.sent > 0 && <div className="small muted">{pct(sum(p.data.waves, "uniqueViews"), sum(p.data.waves, "delivered"))} opened</div>}</>;
}

function SettingsSheet({ onClose }: { onClose: () => void }) {
  const st = useNlSettings();
  const { can } = useSession();
  const toast = useToast();
  const [f, setF] = useState<Partial<NlSettings> | null>(null);
  const [err, setErr] = useState<Error | null>(null);
  useEffect(() => { if (st.data && !f) setF(st.data); }, [st.data, f]);
  const edit = can("newsletter", "edit");
  async function save() {
    setErr(null);
    try {
      await db.rpc("newsletter_settings_save", { p: f });
      invalidate("nl:"); toast("Saved"); onClose();
    } catch (e) { setErr(e as Error); }
  }
  const room = f ? Math.max(0, Math.min(Number(f.daily_cap ?? 0), Number(f.daily_limit ?? 0) - Number(f.reserve ?? 0))) : 0;
  return (
    <Sheet title="Sending settings" onClose={onClose} footer={edit && <button className="btn primary" onClick={save}>Save</button>}>
      {!f ? <Loading /> : <>
        <ErrorBox error={err} />
        <div className="form-grid">
          <Field label="Mailing address (shown in every email)" wide>
            <input className="input" disabled={!edit} value={f.mailing_address ?? ""} onChange={(e) => setF({ ...f, mailing_address: e.target.value })} placeholder="Street, City, Province, Postal code" />
          </Field>
          <Field label="Sender name"><input className="input" disabled={!edit} value={f.sender_name ?? ""} placeholder="Same as automatic emails" onChange={(e) => setF({ ...f, sender_name: e.target.value })} /></Field>
          <Field label="Sender email"><input className="input" disabled={!edit} value={f.sender_email ?? ""} placeholder={f.default_sender ?? ""} onChange={(e) => setF({ ...f, sender_email: e.target.value })} /></Field>
          <Field label="Brevo daily limit (all emails)"><input className="input" type="number" disabled={!edit} value={f.daily_limit ?? 300} onChange={(e) => setF({ ...f, daily_limit: +e.target.value })} /></Field>
          <Field label="Keep free for reminders etc."><input className="input" type="number" disabled={!edit} value={f.reserve ?? 50} onChange={(e) => setF({ ...f, reserve: +e.target.value })} /></Field>
          <Field label="Newsletter emails per day (max)"><input className="input" type="number" disabled={!edit} value={f.daily_cap ?? 250} onChange={(e) => setF({ ...f, daily_cap: +e.target.value })} /></Field>
          <Field label="Start each day's wave at">
            <select className="select" disabled={!edit} value={f.send_hour ?? 10} onChange={(e) => setF({ ...f, send_hour: +e.target.value })}>
              {Array.from({ length: 16 }, (_, i) => i + 6).map((h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
            </select>
          </Field>
        </div>
        <p className="small muted">Up to <b>{n(room)}</b> newsletter emails a day. 2,000 people takes about {Math.ceil(2000 / Math.max(room, 1))} days. Each day's email shows the date it's sent. If you move to a paid Brevo plan, raise the daily limit here.</p>
      </>}
    </Sheet>
  );
}

// ───────────────────────── editor ─────────────────────────
export function NewsletterEditor({ id }: { id: string }) {
  const { can, profile } = useSession();
  const toast = useToast();
  const q = useQuery(`nl:issue:${id}`, () => db.one<Issue>("newsletters", { id: eq(id) }), { staleMs: 0 });
  const [d, setD] = useState<Issue | null>(null);
  const [saved, setSaved] = useState<"" | "saving" | "saved">("");
  const [err, setErr] = useState<Error | null>(null);
  const [rev, setRev] = useState(0);
  const first = useRef(true);
  useEffect(() => { if (q.data && (!d || d.id !== q.data.id)) { setD(q.data); first.current = true; } }, [q.data]);
  const locked = !d || !["Draft", "Scheduled"].includes(d.status) || !can("newsletter", "edit");
  const debounced = useDebounced(d, 700);

  // autosave
  useEffect(() => {
    if (!debounced || locked) return;
    if (first.current) { first.current = false; return; }
    setSaved("saving");
    db.update("newsletters", { id: eq(debounced.id) }, {
      title: debounced.title, subject: debounced.subject, preheader: debounced.preheader || null,
      blocks: debounced.blocks, audience: debounced.audience, daily_cap: debounced.daily_cap,
    }).then(() => { setSaved("saved"); setRev((r) => r + 1); invalidate("nl:list"); }).catch((e) => { setErr(e); setSaved(""); });
  }, [debounced]);

  if (q.error) return <><PageHead help="newsletter" title="Newsletter" /><ErrorBox error={q.error} /></>;
  if (!d) return <><PageHead title="Newsletter" /><Loading rows={6} /></>;
  const set = (patch: Partial<Issue>) => setD({ ...d, ...patch });
  const setBlock = (i: number, b: Block) => set({ blocks: d.blocks.map((x, j) => (j === i ? b : x)) });
  const move = (i: number, by: number) => {
    const bs = [...d.blocks]; const [b] = bs.splice(i, 1); bs.splice(Math.max(0, Math.min(bs.length, i + by)), 0, b); set({ blocks: bs });
  };

  return (
    <>
      <PageHead title={d.subject || "New issue"}
        sub={<><button className="linkish" onClick={() => navigate("/newsletter")}>← All issues</button> · <span className={clsx("pill", STATUS_PILL[d.status])}>{d.status}</span>
          {saved === "saving" ? <span className="muted small"> · saving…</span> : saved === "saved" ? <span className="muted small"> · saved</span> : null}</>} />
      <ErrorBox error={err} />
      {d.error && d.status === "Sending" && <div className="notice">Brevo said: {d.error}. It will try again on the next run (every 10 minutes).</div>}
      <div className="nl-layout">
        <div className="nl-edit">
          <Panel title="The email">
            <div className="panel-body form-grid">
              <Field label="Subject line" wide><input className="input" disabled={locked} value={d.subject} maxLength={150} placeholder="e.g. Your November money notes" onChange={(e) => set({ subject: e.target.value })} /></Field>
              <Field label="Preview text (shown after the subject in the inbox)" wide><input className="input" disabled={locked} value={d.preheader ?? ""} maxLength={150} onChange={(e) => set({ preheader: e.target.value })} /></Field>
              <Field label="Label above the greeting" wide><input className="input" disabled={locked} value={d.title} maxLength={80} onChange={(e) => set({ title: e.target.value })} /></Field>
            </div>
          </Panel>
          <Panel title="Content" hint="Use {first_name} and {date}. Wrap words in **double stars** for bold. Links are made clickable.">
            <div className="panel-body nl-blocks">
              {d.blocks.map((b, i) => (
                <div key={i} className="nl-block">
                  <div className="nl-block-head">
                    <span className="small muted">{BLOCK_LABEL[b.type]}</span>
                    {!locked && <span className="nl-block-tools">
                      <button className="btn small ghost" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up">↑</button>
                      <button className="btn small ghost" disabled={i === d.blocks.length - 1} onClick={() => move(i, 1)} aria-label="Move down">↓</button>
                      <button className="btn small ghost" onClick={() => set({ blocks: d.blocks.filter((_, j) => j !== i) })} aria-label="Remove">✕</button>
                    </span>}
                  </div>
                  <BlockEditor b={b} locked={locked} onChange={(nb) => setBlock(i, nb)} />
                </div>
              ))}
              {!locked && <div className="toolbar" style={{ margin: 0 }}>
                <span className="small muted">Add:</span>
                {(["heading", "text", "image", "button", "divider"] as const).map((t) => (
                  <button key={t} className="btn small" onClick={() => set({ blocks: [...d.blocks, NEW_BLOCK[t]()] })}>+ {BLOCK_LABEL[t]}</button>
                ))}
              </div>}
            </div>
          </Panel>
          <AudiencePanel d={d} locked={locked} onChange={(a) => set({ audience: a })} onCap={(c) => set({ daily_cap: c })} />
          <SendPanel d={d} saving={saved === "saving"} myEmail={profile?.email ?? ""} onChanged={() => { q.reload(); invalidate("nl:"); }} toast={toast} />
        </div>
        <div className="nl-preview">
          <Preview id={d.id} rev={rev} date={d.send_on} />
        </div>
      </div>
    </>
  );
}

const BLOCK_LABEL: Record<Block["type"], string> = { heading: "Heading", text: "Text", image: "Picture", button: "Button", divider: "Divider" };
const NEW_BLOCK: Record<Block["type"], () => Block> = {
  heading: () => ({ type: "heading", text: "" }), text: () => ({ type: "text", text: "" }),
  image: () => ({ type: "image", url: "", alt: "" }), button: () => ({ type: "button", label: "Learn more", url: "" }), divider: () => ({ type: "divider" }),
};

function BlockEditor({ b, locked, onChange }: { b: Block; locked: boolean; onChange: (b: Block) => void }) {
  const [up, setUp] = useState("");
  if (b.type === "heading") return <input className="input" disabled={locked} value={b.text} placeholder="Heading" onChange={(e) => onChange({ ...b, text: e.target.value })} />;
  if (b.type === "text") return <textarea className="textarea" disabled={locked} rows={Math.max(4, b.text.split("\n").length + 1)} value={b.text} onChange={(e) => onChange({ ...b, text: e.target.value })} />;
  if (b.type === "button") return (
    <div className="form-grid">
      <Field label="Button text"><input className="input" disabled={locked} value={b.label} onChange={(e) => onChange({ ...b, label: e.target.value })} /></Field>
      <Field label="Link"><input className="input" disabled={locked} value={b.url} placeholder="https://calendly.com/…" onChange={(e) => onChange({ ...b, url: e.target.value })} /></Field>
    </div>
  );
  if (b.type === "image") {
    const img = b;
    async function pick(file?: File) {
      if (!file) return;
      if (file.size > 3 * 1024 * 1024) { setUp("Pictures must be under 3 MB."); return; }
      setUp("Uploading…");
      try {
        const ext = (file.name.split(".").pop() || "png").toLowerCase().replace(/[^a-z0-9]/g, "");
        const url = await uploadPublic("newsletter", `${dayjs().format("YYYY-MM")}/${crypto.randomUUID()}.${ext}`, file);
        onChange({ ...img, url }); setUp("");
      } catch (e) { setUp((e as Error).message); }
    }
    return (
      <div className="form-grid">
        <Field label="Picture" wide>
          <div className="toolbar" style={{ margin: 0 }}>
            {!locked && <label className="btn small">Upload…<input type="file" accept="image/png,image/jpeg,image/gif,image/webp" hidden onChange={(e) => pick(e.target.files?.[0])} /></label>}
            <input className="input grow" disabled={locked} value={b.url} placeholder="or paste a picture link" onChange={(e) => onChange({ ...b, url: e.target.value })} />
          </div>
          {up && <span className="small muted">{up}</span>}
        </Field>
        <Field label="Describe it (for screen readers)"><input className="input" disabled={locked} value={b.alt ?? ""} onChange={(e) => onChange({ ...b, alt: e.target.value })} /></Field>
        <Field label="Caption (optional)"><input className="input" disabled={locked} value={b.caption ?? ""} onChange={(e) => onChange({ ...b, caption: e.target.value })} /></Field>
        <Field label="Opens this link when clicked (optional)" wide><input className="input" disabled={locked} value={b.link ?? ""} onChange={(e) => onChange({ ...b, link: e.target.value })} /></Field>
      </div>
    );
  }
  return <div className="nl-divider" />;
}

function AudiencePanel({ d, locked, onChange, onCap }: { d: Issue; locked: boolean; onChange: (a: Audience) => void; onCap: (n: number | null) => void }) {
  const tags = useQuery("nl:tags", () => db.select<{ id: string; name: string }>("tags", { select: "id,name", order: "name" }));
  const a: Audience = Object.assign({ clients: true, others: true, tags: [], exclude_tags: [] }, d.audience);
  const key = useDebounced(JSON.stringify([a, d.daily_cap]), 400);
  const cnt = useQuery(`nl:count:${key}`, () => db.rpc<Count>("newsletter_audience_count", { p_audience: a, p_daily_cap: d.daily_cap }));
  const toggle = (k: "tags" | "exclude_tags", tid: string) =>
    onChange({ ...a, [k]: a[k].includes(tid) ? a[k].filter((x) => x !== tid) : [...a[k], tid] });
  const c = cnt.data;
  return (
    <Panel title="Who gets it" hint="Only people who agreed (or who are clients) are ever included. Clients go first.">
      <div className="panel-body">
        <div className="toolbar">
          <label className="check"><input type="checkbox" disabled={locked} checked={a.clients} onChange={(e) => onChange({ ...a, clients: e.target.checked })} /> Clients</label>
          <label className="check"><input type="checkbox" disabled={locked} checked={a.others} onChange={(e) => onChange({ ...a, others: e.target.checked })} /> Leads, team &amp; others who agreed</label>
        </div>
        {!!tags.data?.length && <>
          <div className="small muted">Only people with these tags (leave empty for everyone):</div>
          <div className="chips">{tags.data.map((t) => <button key={t.id} type="button" disabled={locked} className={clsx("chip big", a.tags.includes(t.id) && "on")} onClick={() => toggle("tags", t.id)}>{t.name}</button>)}</div>
          <div className="small muted" style={{ marginTop: 10 }}>Leave out people with these tags:</div>
          <div className="chips">{tags.data.map((t) => <button key={t.id} type="button" disabled={locked} className={clsx("chip big", a.exclude_tags.includes(t.id) && "on off")} onClick={() => toggle("exclude_tags", t.id)}>{t.name}</button>)}</div>
        </>}
        {c && (
          <div className="nl-count">
            <b>{n(c.total)}</b> people · {n(c.clients)} clients, {n(c.others)} others
            {c.total > 0 && <> · <b>{c.days} day{c.days === 1 ? "" : "s"}</b> at up to {n(c.per_day)} a day</>}
            {c.no_consent > 0 && <div className="small muted">{n(c.no_consent)} people with an email aren't included because they haven't agreed. Mark consent on their profile when they say yes.</div>}
          </div>
        )}
        <Field label="Per day for this issue (optional)">
          <input className="input" type="number" min={1} style={{ maxWidth: 140 }} disabled={locked} value={d.daily_cap ?? ""} placeholder={c ? String(c.per_day) : ""}
            onChange={(e) => onCap(e.target.value ? Math.max(1, +e.target.value) : null)} />
        </Field>
      </div>
    </Panel>
  );
}

function SendPanel({ d, saving, myEmail, onChanged, toast }: { d: Issue; saving: boolean; myEmail: string; onChanged: () => void; toast: (m: string) => void }) {
  const { can } = useSession();
  const edit = can("newsletter", "edit");
  const [to, setTo] = useState(myEmail);
  const [on, setOn] = useState(d.send_on ?? dayjs().format("YYYY-MM-DD"));
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const live = d.status === "Sending" || d.status === "Sent" || d.status === "Canceled";
  const prog = useQuery(live ? `nl:progress:${d.id}` : null, () => db.rpc<Progress>("newsletter_progress", { p_id: d.id }), { staleMs: 0 });
  const st = useNlSettings();

  async function act(name: string, fn: () => Promise<any>) {
    setBusy(name); setErr("");
    try {
      const r = await fn();
      if (r && r.ok === false) setErr(r.reason);
      else return r;
    } catch (e) { setErr((e as Error).message); } finally { setBusy(""); }
  }
  const test = () => act("test", () => db.rpc("newsletter_send_test", { p_id: d.id, p_email: to })).then((r) => r && toast(`Test sent to ${r.to}`));
  const schedule = () => act("schedule", () => db.rpc("newsletter_schedule", { p_id: d.id, p_send_on: on })).then((r) => { if (r) { toast("Scheduled"); onChanged(); } });
  const [sure, setSure] = useState(false);
  const stop = () => {
    if (d.status === "Sending" && !sure) { setSure(true); return; }
    setSure(false);
    act("stop", () => db.rpc("newsletter_stop", { p_id: d.id })).then((r) => { if (r) { toast(d.status === "Sending" ? "Stopped. No more waves will go out." : "Back to draft"); onChanged(); } });
  };
  const remove = () => act("delete", async () => { await db.remove("newsletters", { id: eq(d.id) }); invalidate("nl:"); navigate("/newsletter"); });

  const p = prog.data;
  return (
    <Panel title={live ? "Sending" : "Send"}>
      <div className="panel-body">
        {err && <div className="error-box" role="alert">{err}</div>}
        {!live && edit && <>
          <div className="toolbar">
            <input className="input grow" type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="you@example.com" />
            <button className="btn" disabled={!!busy || saving} onClick={test}>{busy === "test" ? "Sending…" : "Send me a test"}</button>
          </div>
          {d.status === "Draft" ? (
            <div className="toolbar">
              <span>Start sending on</span>
              <input className="input" type="date" value={on} min={dayjs().format("YYYY-MM-DD")} onChange={(e) => setOn(e.target.value)} style={{ maxWidth: 180 }} />
              <button className="btn primary" disabled={!!busy || saving} onClick={schedule}>{busy === "schedule" ? "Checking…" : "Schedule"}</button>
              <button className="btn ghost danger" disabled={!!busy} onClick={remove}>Delete draft</button>
            </div>
          ) : (
            <div className="toolbar">
              <span>Starts <b>{fmtDate(d.send_on)}</b> at {hourLabel(st.data?.send_hour ?? 10)}, then a wave each day until everyone has it.</span>
              <button className="btn" disabled={!!busy} onClick={stop}>Unschedule</button>
            </div>
          )}
          <p className="small muted" style={{ margin: 0 }}>Each day's wave is dated the day it goes out. Anyone who unsubscribes is dropped from later waves.</p>
        </>}
        {live && (!p ? <Loading rows={2} /> : <>
          <div className="nl-progress" aria-label={`${p.sent} of ${p.total} sent`}>
            <div className="bar"><span style={{ width: `${p.total ? (p.sent / p.total) * 100 : 0}%` }} /></div>
            <div className="small"><b>{n(p.sent)}</b> of {n(p.total)} sent{p.in_wave ? ` · ${n(p.in_wave)} going out now` : ""}{p.waiting ? ` · ${n(p.waiting)} waiting` : ""}{p.skipped ? ` · ${n(p.skipped)} skipped` : ""}</div>
          </div>
          <div className="nl-stats small-stats">
            <div><b>{pct(sum(p.waves, "uniqueViews"), sum(p.waves, "delivered"))}</b><span>opened</span></div>
            <div><b>{pct(sum(p.waves, "uniqueClicks"), sum(p.waves, "delivered"))}</b><span>clicked</span></div>
            <div><b>{n(sum(p.waves, "unsubscriptions"))}</b><span>unsubscribed</span></div>
            <div><b>{n(sum(p.waves, "hardBounces") + sum(p.waves, "softBounces"))}</b><span>bounced</span></div>
          </div>
          <div className="table-wrap"><table className="data">
            <thead><tr><th>Wave</th><th>Date on the email</th><th className="num">People</th><th>Status</th><th className="num">Opened</th><th className="num">Clicked</th></tr></thead>
            <tbody>{p.waves.map((w) => (
              <tr key={w.wave}>
                <td>{w.wave}</td><td>{fmtDate(w.date, "ddd, MMM D")}</td><td className="num">{n(w.count)}</td>
                <td>{w.status === "Importing" ? "Preparing" : w.status}{w.error && <div className="small" style={{ color: "var(--alert)" }}>{w.error}</div>}</td>
                <td className="num">{w.stats ? pct(w.stats.uniqueViews, w.stats.delivered) : "–"}</td>
                <td className="num">{w.stats ? pct(w.stats.uniqueClicks, w.stats.delivered) : "–"}</td>
              </tr>
            ))}{!p.waves.length && <tr><td colSpan={6} className="muted">The first wave goes out at {hourLabel(st.data?.send_hour ?? 10)}.</td></tr>}</tbody>
          </table></div>
          <p className="small muted">Opens and clicks update every few hours from Brevo.</p>
          {d.status === "Sending" && edit && <div className="toolbar" style={{ margin: 0 }}>
            <button className="btn danger" disabled={!!busy} onClick={stop}>{sure ? "Yes, stop: the rest won't get it" : "Stop sending"}</button>
            {sure && <button className="btn ghost" onClick={() => setSure(false)}>Keep sending</button>}
          </div>}
        </>)}
      </div>
    </Panel>
  );
}

function Preview({ id, rev, date }: { id: string; rev: number; date: string | null }) {
  const q = useQuery(`nl:preview:${id}:${rev}:${date ?? ""}`, () => db.rpc<string>("newsletter_preview", { p_id: id, p_date: date }), { staleMs: 0 });
  const [html, setHtml] = useState("");
  useEffect(() => { if (q.data) setHtml(q.data); }, [q.data]);
  const doc = useMemo(() => html.replace(/<a /g, '<a target="_blank" rel="noopener noreferrer" '), [html]);
  return (
    <div className="nl-preview-frame">
      <div className="small muted nl-preview-label">Preview · how “Ada” sees it</div>
      {!html ? <Loading rows={6} /> : <iframe title="Newsletter preview" sandbox="allow-popups allow-popups-to-escape-sandbox" srcDoc={doc} />}
    </div>
  );
}
